import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { Shield, Lock, Eye, Database, Globe, Mail, Clock, FileText } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      icon: Database,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Personal Information",
          text: "We collect information you provide directly, including name, email address, phone number, and professional details when you create an account, apply for positions, or contact us."
        },
        {
          subtitle: "Usage Data",
          text: "We automatically collect information about how you interact with our platform, including pages visited, features used, time spent, and actions taken during assessments or interviews."
        },
        {
          subtitle: "Device Information",
          text: "We collect device identifiers, browser type, operating system, IP address, and other technical data to ensure platform security and optimize performance."
        }
      ]
    },
    {
      icon: Eye,
      title: "How We Use Your Information",
      content: [
        {
          subtitle: "Service Delivery",
          text: "To provide, maintain, and improve our hiring platform services, including AI-powered candidate matching, assessments, and interview scheduling."
        },
        {
          subtitle: "Communication",
          text: "To send you updates about your applications, platform features, security alerts, and administrative messages essential to your use of Sandgen."
        },
        {
          subtitle: "Analytics & Improvement",
          text: "To analyze usage patterns, measure effectiveness of our features, and continuously enhance the user experience for both employers and candidates."
        }
      ]
    },
    {
      icon: Lock,
      title: "Data Security",
      content: [
        {
          subtitle: "Encryption",
          text: "All data is encrypted using industry-standard 256-bit encryption both in transit (TLS 1.3) and at rest. Your information is protected by enterprise-grade security measures."
        },
        {
          subtitle: "Access Controls",
          text: "We implement strict role-based access controls, multi-factor authentication, and regular security audits to ensure only authorized personnel can access sensitive data."
        },
        {
          subtitle: "Compliance",
          text: "Sandgen is SOC 2 Type II certified and fully compliant with GDPR, CCPA, and other applicable data protection regulations."
        }
      ]
    },
    {
      icon: Globe,
      title: "Data Sharing & Third Parties",
      content: [
        {
          subtitle: "Employers & Candidates",
          text: "Information is shared between employers and candidates only as necessary to facilitate the hiring process and only with explicit consent."
        },
        {
          subtitle: "Service Providers",
          text: "We work with trusted third-party providers for hosting, analytics, and communication services. All partners are bound by strict data protection agreements."
        },
        {
          subtitle: "Legal Requirements",
          text: "We may disclose information when required by law, to protect our rights, prevent fraud, or respond to legal requests from authorities."
        }
      ]
    },
    {
      icon: Shield,
      title: "Your Rights & Choices",
      content: [
        {
          subtitle: "Access & Portability",
          text: "You have the right to access, download, and receive a copy of your personal data in a portable format at any time through your account settings."
        },
        {
          subtitle: "Correction & Deletion",
          text: "You can update your information directly in your account or request deletion of your data. We will respond to deletion requests within 30 days."
        },
        {
          subtitle: "Opt-Out Options",
          text: "You can opt out of marketing communications, adjust cookie preferences, and control how your data is used for analytics through your privacy settings."
        }
      ]
    },
    {
      icon: Clock,
      title: "Data Retention",
      content: [
        {
          subtitle: "Active Accounts",
          text: "We retain your data for as long as your account is active or as needed to provide services. You can request deletion at any time."
        },
        {
          subtitle: "Inactive Accounts",
          text: "For inactive accounts, we retain data for up to 3 years to allow you to return without losing your history, after which data is automatically purged."
        },
        {
          subtitle: "Legal Obligations",
          text: "Certain data may be retained longer to comply with legal obligations, resolve disputes, or enforce our agreements as required by law."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Legal</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              Your privacy matters. Here&apos;s exactly how we collect, use, and protect your data.
              No legal jargon—just clear answers.
            </p>
            <p className="text-sm text-neutral-400 mt-6">
              Last updated: February 5, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-neutral-50 border-y border-neutral-200">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-black mb-3">Our Commitment</h2>
                <p className="text-neutral-600 leading-relaxed">
                  At Sandgen, we believe that trust is the foundation of every great relationship—including the one
                  between us and our users. This Privacy Policy explains how Sandgen Technologies, Inc.
                  (&quot;Sandgen,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses, shares, and protects your personal
                  information when you use our hiring platform and services. By using Sandgen, you agree to the
                  practices described in this policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-16">
            {sections.map((section, index) => (
              <div key={index} className="border-b border-neutral-200 pb-16 last:border-0 last:pb-0">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-black" />
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl text-black tracking-tight">
                    {section.title}
                  </h2>
                </div>
                <div className="space-y-8 pl-0 md:pl-16">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <h3 className="text-black font-semibold mb-2">{item.subtitle}</h3>
                      <p className="text-neutral-600 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
